import React, { useCallback, useMemo, useState, type FC } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';
import type {
	KnowledgeBaseArticleSearchSortByKey,
	KnowledgeBaseArticleSearchSortOrder,
	viewArticlesTableQuery,
} from '@atlassian/jira-relay/src/__generated__/viewArticlesTableQuery.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller/src/components/cloud-id/index.tsx';
import KnowledgeHubLoadingError from '@atlassian/jira-servicedesk-knowledge-hub/src/view/knowledge-hub-loading-error/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import {
	createLinkedSpaceColumnGeneric,
	createLinkedSpaceColumnWithoutLinkGeneric,
	createTitleColumnGeneric,
	lastModifiedColumnGeneric,
} from '../../common/knowledge-table/knowledge-table-column/view.tsx';
import { KnowledgeTableGraphql } from '../../common/knowledge-table/knowledge-table-graphql/index.tsx';
import { PACKAGE_NAME } from '../../constants.tsx';
import { TEAM_NAME } from '../../common/constants.tsx';
import type { AllArticleTableProps } from './types.tsx';

type Knowledge = {
	id: string;
	title: string;
	url?: string | null;
	spaceName?: string | null;
	spaceUrl?: string | null;
	spaceAri?: string | null;
	lastModified: string;
};

export const ArticleTable: FC<AllArticleTableProps> = ({
	canViewSpaceUrl,
	searchQuery,
	emptyStateComponent,
	noSearchResultsComponent,
	spaceAriList: articleContainers,
	projectId,
	showSpaceColumn,
	limit = 25,
}) => {
	const cloudId = useCloudId();
	const [sortKey, setSortKey] = useState<KnowledgeBaseArticleSearchSortByKey>('LAST_MODIFIED');
	const [sortOrder, setSortOrder] = useState<KnowledgeBaseArticleSearchSortOrder>('DESC');
	const [pageNumber, setPageNumber] = useState<number>(1);
	const [cursor, setCursor] = useState<string | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// we must reset cursor whenever search query or space changes.
	useMemo(() => {
		setCursor(null);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchQuery, articleContainers]);

	const searchArticles = useLazyLoadQuery<viewArticlesTableQuery>(
		graphql`
			query viewArticlesTableQuery($searchInput: KnowledgeBaseArticleSearchInput) {
				knowledgeBase_searchArticles(searchInput: $searchInput) {
					__typename
					... on KnowledgeBaseCrossSiteSearchConnection {
						nodes {
							lastModified
							id
							spaceName
							spaceUrl
							title
							url
						}
						pageInfo {
							hasNextPage
							endCursor
							hasPreviousPage
							startCursor
						}
					}
					... on QueryError {
						message
					}
				}
			}
		`,
		{
			searchInput: {
				searchQuery,
				projectId,
				cloudId,
				cursor,
				articleContainers,
				sortByKey: sortKey,
				sortOrder,
				limit,
			},
		},
		{
			fetchPolicy: 'network-only', // TODO: Add PageNumber to server schema to cache the data in relay store
		},
	);

	const { pageInfo, nodes } = useMemo(() => {
		if (
			searchArticles.knowledgeBase_searchArticles?.__typename ===
			'KnowledgeBaseCrossSiteSearchConnection'
		) {
			fireTrackAnalytics(createAnalyticsEvent({}), 'fetchCrossSiteArticles succeeded');
			return {
				pageInfo: searchArticles.knowledgeBase_searchArticles.pageInfo,
				nodes: searchArticles.knowledgeBase_searchArticles.nodes,
			};
		}
		return {
			pageInfo: null,
			nodes: [],
		};
	}, [createAnalyticsEvent, searchArticles.knowledgeBase_searchArticles]);

	useMemo(() => {
		if (!pageInfo?.hasPreviousPage) {
			setPageNumber(1);
		}
	}, [pageInfo]);

	const knowledgePieceData = useMemo(() => {
		return (
			nodes
				?.map((articleData) => {
					if (!articleData?.id || !articleData.title || !articleData.lastModified) return null;
					return {
						id: articleData?.id,
						title: articleData?.title,
						url: articleData?.url,
						spaceName: articleData?.spaceName,
						spaceUrl: articleData?.spaceUrl,
						lastModified: articleData?.lastModified,
					};
				})
				.filter(Boolean) ?? []
		);
	}, [nodes]);

	const columns = useMemo(() => {
		return [
			createTitleColumnGeneric<Knowledge>(
				(knowledge) => (knowledge?.url ? knowledge.url : '#'),
				60,
			),
			...(showSpaceColumn
				? [
						canViewSpaceUrl
							? createLinkedSpaceColumnGeneric<Knowledge>(30)
							: createLinkedSpaceColumnWithoutLinkGeneric<Knowledge>(30),
					]
				: []),
			lastModifiedColumnGeneric<Knowledge>(10),
		];
	}, [canViewSpaceUrl, showSpaceColumn]);

	const onSort = useCallback(
		(
			newSortKey: KnowledgeBaseArticleSearchSortByKey,
			newSortOrder: KnowledgeBaseArticleSearchSortOrder,
		) => {
			setSortKey(newSortKey);
			setSortOrder(newSortOrder);
		},
		[],
	);

	const loadPrevious = useCallback(() => {
		if (pageInfo?.hasPreviousPage && pageInfo?.startCursor) {
			setCursor(pageInfo.startCursor);
			setPageNumber((_page) => Math.max(0, _page - 1));
		}
	}, [pageInfo?.hasPreviousPage, pageInfo?.startCursor]);

	const loadNext = useCallback(() => {
		if (pageInfo?.hasNextPage && pageInfo.endCursor) {
			setCursor(pageInfo.endCursor);
			setPageNumber((_page) => _page + 1);
		}
	}, [pageInfo?.endCursor, pageInfo?.hasNextPage]);

	if (searchArticles.knowledgeBase_searchArticles?.__typename === 'QueryError') {
		fireErrorAnalytics({
			meta: {
				id: 'fetchCrossSiteArticles',
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			attributes: {
				message: searchArticles.knowledgeBase_searchArticles.message,
			},
		});
		return <KnowledgeHubLoadingError projectKey={projectId} enableHeader={false} />;
	}

	return (
		<UFOSegment name="knowledge-base-hub-table-cross-site">
			<KnowledgeTableGraphql<Knowledge>
				columns={columns}
				totalSize={knowledgePieceData.length}
				knowledgePieces={knowledgePieceData}
				limit={limit}
				page={pageNumber}
				loadNext={loadNext}
				loadPrevious={loadPrevious}
				onSort={onSort}
				searchQuery={searchQuery}
				sortBy={sortKey}
				sortOrder={sortOrder}
				categoryId="allArticles"
				emptyStateComponent={emptyStateComponent}
				noSearchResultsComponent={noSearchResultsComponent}
				nextCursor={pageInfo?.endCursor ?? null}
				prevCursor={pageInfo?.startCursor ?? null}
			/>
		</UFOSegment>
	);
};
